/**
 * Agora AI Noise Suppression Service
 *
 * Based on: https://docs.agora.io/en/video-calling/advanced-features/ai-noise-suppression
 * Package: agora-extension-ai-denoiser
 *
 * Modes:
 * - 'STATIONARY_NS': Removes stationary noise (fans, AC)
 * - 'NSNG': AI noise gate - removes all non-speech sounds (recommended)
 *
 * Levels:
 * - 'SOFT': Standard suppression (recommended)
 * - 'AGGRESSIVE': Aggressive suppression
 */

import AgoraRTC from 'agora-rtc-sdk-ng';
import { AIDenoiserExtension } from 'agora-extension-ai-denoiser';

// Singleton state
let denoiserExtension = null;
let denoiserProcessor = null;
let isExtensionRegistered = false;
let isProcessorReady = false;
//let currentMode = 'STATIONARY_NS';
let currentMode = 'NSNG';
let currentLevel = 'SOFT';
let isEnabled = false;
let currentAudioTrack = null;
let initPromise = null;

// Noise cancellation levels
export const NOISE_CANCELLATION_LEVELS = {
  OFF: { enabled: false, mode: null, label: 'Off' },
  LOW: { enabled: true, mode: 'STATIONARY_NS', level: 'SOFT', label: 'Low' },
  HIGH: { enabled: true, mode: 'NSNG', level: 'SOFT', label: 'High (AI)' },
};

/**
 * Register the AI Denoiser extension with AgoraRTC
 * MUST be called BEFORE creating any audio tracks
 */
export function registerDenoiserExtension() {
  if (isExtensionRegistered) {
    console.log('[NoiseCancellation] Extension already registered');
    return true;
  }

  try {
    console.log('[NoiseCancellation] Creating and registering AI Denoiser extension...');

    // Create extension - WASM files served from /wasm/
    denoiserExtension = new AIDenoiserExtension({
      assetsPath: '/wasm/',
    });

    // Error handler for WASM loading failures
    denoiserExtension.onloaderror = (error) => {
      console.error('[NoiseCancellation] WASM loader error:', error);
      isExtensionRegistered = false;
    };

    // Register with AgoraRTC - MUST happen before createMicrophoneAudioTrack()
    AgoraRTC.registerExtensions([denoiserExtension]);

    isExtensionRegistered = true;
    console.log('[NoiseCancellation] ✓ Extension registered with AgoraRTC');
    return true;
  } catch (error) {
    console.error('[NoiseCancellation] Registration failed:', error);
    return false;
  }
}

/**
 * Ensure processor is created and initialized
 */
async function ensureProcessorReady() {
  if (isProcessorReady && denoiserProcessor) {
    return true;
  }

  if (initPromise) {
    return initPromise;
  }

  initPromise = (async () => {
    try {
      if (!isExtensionRegistered || !denoiserExtension) {
        console.error('[NoiseCancellation] Extension not registered');
        return false;
      }

      console.log('[NoiseCancellation] Creating processor...');
      denoiserProcessor = denoiserExtension.createProcessor();

      // Handle processor overload (CPU can't keep up)
      denoiserProcessor.onoverload = async (elapsedTimeInMs) => {
        console.warn('[NoiseCancellation] Processor overload!', elapsedTimeInMs, 'ms');
      };

      // Listen for WASM load errors on the processor
      denoiserProcessor.on?.('loaderror', (error) => {
        console.error('[NoiseCancellation] Processor WASM load error:', error);
        isProcessorReady = false;
      });

      // Note: The processor does NOT have an init() method.
      // It initializes internally when enable() is called.
      // The WASM is loaded at that point.
      isProcessorReady = true;
      console.log('[NoiseCancellation] ✓ Processor created and ready');
      return true;
    } catch (error) {
      console.error('[NoiseCancellation] Processor creation failed:', error);
      isProcessorReady = false;
      denoiserProcessor = null;
      return false;
    } finally {
      initPromise = null;
    }
  })();

  return initPromise;
}

/**
 * Initialize noise cancellation
 */
export async function initializeNoiseCancellation() {
  if (!isExtensionRegistered) {
    registerDenoiserExtension();
  }
  return ensureProcessorReady();
}

/**
 * Enable noise cancellation on an audio track
 * @param {ILocalAudioTrack} audioTrack - Agora audio track
 * @param {string} mode - 'NSNG' or 'STATIONARY_NS'
 * @param {string} level - 'SOFT' or 'AGGRESSIVE'
 */
export async function enableNoiseCancellation(audioTrack, mode = 'STATIONARY_NS', level = 'SOFT') {
  console.log('[NoiseCancellation] enableNoiseCancellation called');
  console.log('[NoiseCancellation] - track:', !!audioTrack);
  console.log('[NoiseCancellation] - mode:', mode);
  console.log('[NoiseCancellation] - level:', level);

  if (!audioTrack) {
    console.warn('[NoiseCancellation] No audio track provided');
    return false;
  }

  const ready = await ensureProcessorReady();
  if (!ready || !denoiserProcessor) {
    console.error('[NoiseCancellation] Processor not ready');
    return false;
  }

  try {
    // If switching tracks, unpipe old one first
    if (currentAudioTrack && currentAudioTrack !== audioTrack && isEnabled) {
      console.log('[NoiseCancellation] Switching tracks - disabling old one');
      try {
        await denoiserProcessor.disable();
        currentAudioTrack.unpipe();
      } catch (e) {
        console.warn('[NoiseCancellation] Error during track switch:', e);
      }
    } else if (currentAudioTrack === audioTrack && isEnabled) {
      // Same track, already enabled - just update mode/level
      console.log('[NoiseCancellation] Same track, updating mode/level');
      await denoiserProcessor.setMode(mode);
      currentMode = mode;
      await denoiserProcessor.setLevel(level);
      currentLevel = level;
      return true;
    }

    // IMPORTANT: Correct order per Agora docs:
    // 1. pipe() - connect processor to audio pipeline
    // 2. enable() - loads WASM and starts processing
    // 3. setMode()/setLevel() - configure AFTER WASM is loaded

    // Step 1: Pipe audio track through the processor
    console.log('[NoiseCancellation] Piping audio track through processor...');
    audioTrack.pipe(denoiserProcessor).pipe(audioTrack.processorDestination);

    // Step 2: Enable the processor (this loads the WASM)
    console.log('[NoiseCancellation] Enabling processor (loading WASM)...');
    await denoiserProcessor.enable();

    // Step 3: Set mode AFTER enable (WASM must be loaded first)
    console.log('[NoiseCancellation] Setting mode:', mode);
    await denoiserProcessor.setMode(mode);
    currentMode = mode;

    // Step 4: Set level AFTER enable
    console.log('[NoiseCancellation] Setting level:', level);
    await denoiserProcessor.setLevel(level);
    currentLevel = level;

    currentAudioTrack = audioTrack;
    isEnabled = true;

    console.log('[NoiseCancellation] ✓ AI Noise Suppression ENABLED');
    console.log('[NoiseCancellation]   Mode:', mode);
    console.log('[NoiseCancellation]   Level:', level);
    return true;
  } catch (error) {
    console.error('[NoiseCancellation] Enable failed:', error);
    isEnabled = false;
    return false;
  }
}

/**
 * Disable noise cancellation
 */
export async function disableNoiseCancellation(audioTrack) {
  console.log('[NoiseCancellation] disableNoiseCancellation called');

  if (!denoiserProcessor) {
    isEnabled = false;
    return true;
  }

  try {
    await denoiserProcessor.disable();

    const track = audioTrack || currentAudioTrack;
    if (track) {
      try {
        track.unpipe();
      } catch (e) {
        // Ignore unpipe errors
      }
    }

    currentAudioTrack = null;
    isEnabled = false;
    console.log('[NoiseCancellation] ✓ Disabled');
    return true;
  } catch (error) {
    console.error('[NoiseCancellation] Disable failed:', error);
    return false;
  }
}

/**
 * Set noise cancellation mode
 */
export async function setNoiseCancellationMode(mode) {
  console.log('[NoiseCancellation] setMode:', mode);
  currentMode = mode;

  if (denoiserProcessor && isEnabled) {
    try {
      await denoiserProcessor.setMode(mode);
      return true;
    } catch (error) {
      console.error('[NoiseCancellation] setMode failed:', error);
      return false;
    }
  }
  return true;
}

/**
 * Set noise cancellation level
 */
export async function setNoiseCancellationLevel(level) {
  console.log('[NoiseCancellation] setLevel:', level);
  currentLevel = level;

  if (denoiserProcessor && isEnabled) {
    try {
      await denoiserProcessor.setLevel(level);
      return true;
    } catch (error) {
      console.error('[NoiseCancellation] setLevel failed:', error);
      return false;
    }
  }
  return true;
}

/**
 * Verify the processor is piped to the track the caller currently considers
 * "active" (i.e. the one being published to the channel). After setDevice()
 * the track reference stays the same, so this is a no-op in the happy path.
 * But on paths that recreate the track (mobile changeMic, reconnect republish)
 * the singleton `currentAudioTrack` can drift out of sync with the published
 * track, leaving the processor piped to a closed track and the channel raw.
 *
 * Returns true if already attached correctly, or after a successful re-pipe.
 */
export async function verifyDenoiserAttached(audioTrack, mode = currentMode, level = currentLevel) {
  if (!audioTrack) return false;
  // If NC was never enabled, nothing to verify.
  if (!isEnabled) return true;
  // If already piped to this track, we're fine.
  if (currentAudioTrack === audioTrack) return true;
  console.warn('[NoiseCancellation] verifyDenoiserAttached: drift detected — re-piping to active track');
  return enableNoiseCancellation(audioTrack, mode, level);
}

/**
 * Get current status
 */
export function getNoiseCancellationStatus() {
  return {
    isExtensionRegistered,
    isProcessorReady,
    isEnabled,
    currentMode,
    currentLevel,
    hasTrack: !!currentAudioTrack,
  };
}

/**
 * Cleanup resources
 */
export async function cleanupNoiseCancellation() {
  console.log('[NoiseCancellation] Cleanup');
  try {
    if (denoiserProcessor && isEnabled) {
      await denoiserProcessor.disable();
    }
    if (currentAudioTrack) {
      try {
        currentAudioTrack.unpipe();
      } catch (e) {}
    }
  } catch (e) {
    console.warn('[NoiseCancellation] Cleanup error:', e);
  }
  currentAudioTrack = null;
  isEnabled = false;
}

export default {
  registerDenoiserExtension,
  initializeNoiseCancellation,
  enableNoiseCancellation,
  disableNoiseCancellation,
  setNoiseCancellationMode,
  setNoiseCancellationLevel,
  verifyDenoiserAttached,
  getNoiseCancellationStatus,
  cleanupNoiseCancellation,
  NOISE_CANCELLATION_LEVELS,
};
